import { useEffect, useMemo, useState } from "react";
import { Users, Search, Mail, MessageCircle, Package, Wallet, Clock, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function ClientsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | shipments | deposits | requests

  useEffect(() => {
    setLoading(true);
    api.get("/admin/clients-directory")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const search = q.trim().toLowerCase();
    return data.clients.filter((c) => {
      if (filter === "shipments" && !c.shipments) return false;
      if (filter === "deposits" && !c.deposits) return false;
      if (filter === "requests" && !c.requests) return false;
      if (!search) return true;
      const hay = `${c.name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
      return hay.includes(search);
    });
  }, [data, q, filter]);

  const totals = useMemo(() => {
    if (!data) return { clients: 0, ships: 0, deps: 0, reqs: 0 };
    return data.clients.reduce((acc, c) => ({
      clients: acc.clients + 1,
      ships: acc.ships + (c.shipments || 0),
      deps: acc.deps + (c.deposits || 0),
      reqs: acc.reqs + (c.requests || 0),
    }), { clients: 0, ships: 0, deps: 0, reqs: 0 });
  }, [data]);

  if (loading) return <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)]"><Loader2 className="animate-spin" size={16}/> Chargement du répertoire…</div>;
  if (err) return <p className="text-sm text-red-500">{err}</p>;

  const waLink = (phone) => `https://wa.me/${String(phone).replace(/[^\d]/g, "")}`;

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Users} label="Clients uniques" value={totals.clients} color="var(--accent)" />
        <Stat icon={Package} label="Total expéditions" value={totals.ships} color="var(--secondary)" />
        <Stat icon={Wallet} label="Total dépôts" value={totals.deps} color="#00A86B" />
        <Stat icon={Clock} label="Demandes en attente" value={totals.reqs} color="#FF6B00" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
          <input
            placeholder="Rechercher par nom, email ou téléphone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="input-field pl-10 w-full"
            data-testid="clients-search"
          />
        </div>
        <div className="flex gap-1 bg-[var(--bg-soft)] rounded-full p-1">
          {[
            { k: "all", l: "Tous" },
            { k: "shipments", l: "Fret" },
            { k: "deposits", l: "Transferts" },
            { k: "requests", l: "Demandes" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setFilter(o.k)}
              data-testid={`clients-filter-${o.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${filter === o.k ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-[var(--ink-soft)]">
          {filtered.length} sur {data.total} clients
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="card-flat p-10 text-center text-sm text-[var(--ink-soft)]">Aucun client ne correspond à votre recherche.</div>
        ) : filtered.map((c) => (
          <div key={(c.email || c.phone || c.name) + c.first_seen} className="card-flat p-4" data-testid={`client-${(c.email || c.phone || c.name).replace(/[^a-z0-9]/gi, "-")}`}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--secondary)] text-white flex items-center justify-center font-display font-black text-sm flex-shrink-0">
                {(c.name || c.email || "?").trim()[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold">{c.name || c.email || c.phone || "—"}</p>
                  {c.registered && (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-green-500/10 text-green-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <CheckCircle2 size={10} /> Compte
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--ink-soft)] mt-1">
                  {c.email && <span className="inline-flex items-center gap-1"><Mail size={10}/> {c.email}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1">📱 +{c.phone}</span>}
                  <span>1er contact : {formatDate(c.first_seen)}</span>
                  <span>Dernier : {formatDate(c.last_seen)}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {c.shipments > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--secondary)]/10 text-[var(--secondary)] px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <Package size={11}/> {c.shipments} fret{c.shipments > 1 ? "s" : ""}
                  </span>
                )}
                {c.deposits > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[#00A86B]/10 text-[#00A86B] px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <Wallet size={11}/> {c.deposits} dépôt{c.deposits > 1 ? "s" : ""}
                  </span>
                )}
                {c.requests > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6B00]/10 text-[#FF6B00] px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <Clock size={11}/> {c.requests} demande{c.requests > 1 ? "s" : ""}
                  </span>
                )}
                {c.phone && (
                  <a href={waLink(c.phone)} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white text-xs px-3 py-1 rounded font-bold inline-flex items-center gap-1" title="Contacter sur WhatsApp">
                    <MessageCircle size={12}/>
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="btn-outline !py-1 !px-2 text-xs" title="Envoyer un email">
                    <Mail size={12}/>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="card-flat p-5">
      <Icon size={18} style={{ color }} />
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">{label}</p>
      <p className="font-display text-2xl font-black mt-1">{value}</p>
    </div>
  );
}
